/**
 * Centralized Query Cache Management
 * 
 * SINGLE SOURCE OF TRUTH for all TanStack Query cache keys and invalidation patterns.
 * This ensures consistent data across all pages after mutations/syncs complete.
 * 
 * ⚠️ IMPORTANT: When adding new queries, register them here to prevent cache inconsistencies.
 * 
 * USAGE:
 *   import { QueryKeys, useInvalidateCache } from '../lib/query-cache'
 *   
 *   // In a component:
 *   const invalidate = useInvalidateCache()
 *   invalidate.dtc.all()  // Invalidates all DTC queries
 *   invalidate.dtc.kpis() // Invalidates just KPI queries
 */

import { useQueryClient, QueryClient } from '@tanstack/react-query'

// ============================================================================
// QUERY KEY CONSTANTS
// ============================================================================

/**
 * All query keys used in the application.
 * Organized by domain for easier maintenance.
 */
export const QueryKeys = {
  // DTC (Direct-to-Consumer) Domain
  dtc: {
    all: ['dtc'] as const,
    kpis: ['dtc', 'kpis'] as const,
    products: ['dtc', 'products'] as const,
    customers: ['dtc', 'customers'] as const,
    platforms: ['dtc', 'platforms'] as const,
    syncStatus: (platformId: string) => ['dtc', 'sync-status', platformId] as const,
    insights: ['dtc-insights'] as const,
  },
  
  // Retail Analytics Domain
  retail: {
    all: ['retail'] as const,
    kpis: ['kpis'] as const,
    alerts: ['alerts'] as const,
    stores: ['stores'] as const,
    distribution: ['distribution'] as const,
    promotions: ['promotions'] as const,
    forecast: ['forecast'] as const,
  },
  
  // Admin Domain
  admin: {
    workflows: ['workflows'] as const,
    policies: ['tenant-policies'] as const,
    agents: ['tenant-agent-configs'] as const,
    apiKeys: ['api-keys'] as const,
    knowledge: ['knowledge-docs'] as const,
    profiles: {
      tenant: ['tenant-profile'] as const,
      user: ['user-profile'] as const,
    },
  },
  
  // Insights Domain
  insights: {
    proactive: ['proactive-insights'] as const,
    generated: ['insights-generated'] as const,
  },
  
  // Copilot Domain
  copilot: {
    feedback: {
      analytics: ['copilot-feedback-analytics'] as const,
      list: ['copilot-feedback-list'] as const,
    },
  },
  
  // Advocate Engine Domain (Opus 4.5 Build)
  advocateOpus: {
    all: ['advocate-opus'] as const,
    dashboard: ['advocate-opus', 'dashboard'] as const,
    mentions: ['advocate-opus', 'mentions'] as const,
    advocates: ['advocate-opus', 'advocates'] as const,
    advocate: (id: string) => ['advocate-opus', 'advocate', id] as const,
    offers: ['advocate-opus', 'offers'] as const,
    links: ['advocate-opus', 'links'] as const,
    conversions: ['advocate-opus', 'conversions'] as const,
    payouts: ['advocate-opus', 'payouts'] as const,
  },

  // Advocate Engine Domain
  advocate: {
    list: (filters?: string) => ['advocate', 'list', filters || 'all'] as const,
    profile: (advocateId: string) => ['advocate', 'profile', advocateId] as const,
    offers: ['advocate', 'offers'] as const,
    payouts: ['advocate', 'payouts'] as const,
    reporting: ['advocate', 'reporting'] as const,
  },
} as const

// ============================================================================
// INVALIDATION FUNCTIONS
// ============================================================================

/**
 * Creates invalidation functions for a specific query client.
 * Use this to invalidate related queries together.
 */
function createInvalidators(queryClient: QueryClient) {
  return {
    /**
     * DTC Domain - Invalidates all queries related to DTC sales/platforms
     */
    dtc: {
      /** Invalidate ALL DTC queries (use after major sync) */
      all: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.all })
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.insights })
      },
      
      /** Invalidate just KPI data (revenue, orders, etc.) */
      kpis: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.kpis })
      },
      
      /** Invalidate after Shopify/Amazon sync completes */
      afterSync: (platformId?: string) => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.kpis })
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.products })
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.customers })
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.platforms })
        if (platformId) {
          queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.syncStatus(platformId) })
        }
      },
      
      /** Invalidate after aggregation/refresh */
      afterAggregation: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.kpis })
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.platforms })
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.insights })
      },
    },
    
    /**
     * Retail Domain - Invalidates retail analytics queries
     */
    retail: {
      /** Invalidate ALL retail queries */
      all: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.retail.all })
      },
      
      /** Invalidate after data upload/refresh */
      afterDataLoad: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.retail.kpis })
        queryClient.invalidateQueries({ queryKey: QueryKeys.retail.alerts })
        queryClient.invalidateQueries({ queryKey: QueryKeys.retail.distribution })
        queryClient.invalidateQueries({ queryKey: QueryKeys.retail.promotions })
      },
    },
    
    /**
     * Insights Domain
     */
    insights: {
      all: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.insights.proactive })
        queryClient.invalidateQueries({ queryKey: QueryKeys.insights.generated })
        queryClient.invalidateQueries({ queryKey: QueryKeys.dtc.insights })
      },
    },
    
    /**
     * Admin Domain
     */
    admin: {
      all: () => {
        Object.values(QueryKeys.admin).forEach(key => {
          if (Array.isArray(key)) {
            queryClient.invalidateQueries({ queryKey: key })
          }
        })
      },
    },
    
    /**
     * Advocate Engine Domain (Opus 4.5 Build)
     */
    advocateOpus: {
      /** Invalidate ALL advocate queries */
      all: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.all })
      },
      
      /** Invalidate after mention ingestion */
      afterIngest: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.mentions })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.advocates })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.dashboard })
      },
      
      /** Invalidate after scoring recalculation */
      afterScoring: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.advocates })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.dashboard })
      },
      
      /** Invalidate after conversion tracking */
      afterConversion: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.conversions })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.payouts })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.advocates })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.dashboard })
      },
      
      /** Invalidate after payout processing */
      afterPayout: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.payouts })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.advocates })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocateOpus.dashboard })
      },
    },

    advocate: {
      all: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.list('all') })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.reporting })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.offers })
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.payouts })
      },
      list: (filters?: string) => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.list(filters) })
      },
      profile: (advocateId: string) => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.profile(advocateId) })
      },
      reporting: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.reporting })
      },
      offers: () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.advocate.offers })
      },
    },
  }
}

// ============================================================================
// REACT HOOK
// ============================================================================

/**
 * Hook to get cache invalidation functions.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const invalidate = useInvalidateCache()
 *   
 *   const handleSyncComplete = () => {
 *     invalidate.dtc.afterSync('platform-123')
 *   }
 * }
 * ```
 */
export function useInvalidateCache() {
  const queryClient = useQueryClient()
  return createInvalidators(queryClient)
}

/**
 * Standalone function for use outside React components (e.g., in data services)
 * 
 * @example
 * ```ts
 * // In a mutation's onSuccess:
 * onSuccess: () => {
 *   const invalidators = getInvalidators(queryClient)
 *   invalidators.dtc.afterSync()
 * }
 * ```
 */
export function getInvalidators(queryClient: QueryClient) {
  return createInvalidators(queryClient)
}

// ============================================================================
// DOCUMENTATION
// ============================================================================

/**
 * CACHE INVALIDATION BEST PRACTICES
 * ==================================
 * 
 * 1. ALWAYS invalidate related queries together
 *    - If you update DTC sales, invalidate KPIs AND insights
 *    - Use the domain-specific functions like `invalidate.dtc.afterSync()`
 * 
 * 2. Use the centralized QueryKeys
 *    - Don't hardcode query keys in components
 *    - Import from this file: import { QueryKeys } from '../lib/query-cache'
 * 
 * 3. After any mutation that changes data:
 *    - Call the appropriate invalidation function in onSuccess
 *    - Consider what other views might be affected
 * 
 * 4. When adding new queries:
 *    - Add the key to QueryKeys above
 *    - Add to relevant invalidation functions
 * 
 * COMMON PATTERNS:
 * ----------------
 * 
 * // After sync completes (use in useEffect watching sync status)
 * if (syncStatus === 'success') {
 *   invalidate.dtc.afterSync(platformId)
 * }
 * 
 * // After manual data refresh
 * onSuccess: () => {
 *   invalidate.dtc.afterAggregation()
 * }
 * 
 * // After generating insights
 * onSuccess: () => {
 *   invalidate.insights.all()
 * }
 */

